<template>
  <div class="unmatched-card" :class="[isEditable ? 'editable' : '']">
    <div class="main-wrapper">
      <div v-if="isEditable" class="delete-button um-invisible">
        <span
          @click="$emit('delete:card', $event)"
          class="fa-layers fa-fw fa-2x"
        >
          <font-awesome-icon :icon="['fas', 'circle']" />
          <font-awesome-icon
            :icon="['fas', 'trash-alt']"
            :style="{ color: 'white' }"
            transform="shrink-6"
          />
        </span>
      </div>
      <div class="hero panel">
        <div class="name section">
          <div class="content xl" @click="focusEditableText">
            <span
              v-text="title"
              class="scale-text"
              :contenteditable="isEditable"
              spellcheck="false"
              @input="scaleText($event.target)"
              @blur="updateEditableText('title', $event)"
              @keypress.13="
                $event.preventDefault();
                $event.target.blur();
              "
            ></span>
          </div>
        </div>
      </div>

      <div class="abilities panel">
        <div class="special section">
          <div class="content">
            <span
              v-text="content"
              :contenteditable="isEditable"
              style="white-space: pre-wrap"
              spellcheck="false"
              class="mixed-case"
              @blur="updateEditableText('content', $event)"
            ></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import editable from "@/mixins/editable.js";

export default {
  name: "UnmatchedRuleCard",

  mixins: [editable],
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
    },
    content: {
      type: String,
    },
  },
  data: function () {
    return {
      initialWidth: undefined,
    };
  },
  computed: {},
  mounted: function () {
    this.$nextTick(function () {
      const heroNameText = this.$el.querySelector(".hero .name .xl.content");
      const computedStyle = window.getComputedStyle(heroNameText);
      const paddingLeft = parseFloat(
        computedStyle.paddingLeft.replace("px", "")
      );
      const paddingRight = parseFloat(
        computedStyle.paddingRight.replace("px", "")
      );
      const width = parseFloat(computedStyle.width.replace("px", ""));

      this.initialWidth = width - paddingLeft - paddingRight;

      this.$nextTick(() => {
        document.fonts.ready.then(() => {
          this.scaleText(".hero .name .xl.content span");
        });
      });
    });
  },
  watch: {
    heroName: function () {
      this.$nextTick(() => {
        this.scaleText(".hero .name .xl.content span");
      });
    },
  },
  methods: {
    scaleText: function (selector) {
      const textToScale =
        selector instanceof HTMLElement
          ? selector
          : this.$el.querySelector(selector);
      // const computedStyle = window.getComputedStyle(textToScale);
      if (!textToScale) {
        return;
      }
      const width = textToScale.offsetWidth;

      // Need to check width because for some reason
      // this gets fired twice, once with the width=0,
      // which would reset the scale to (1) every time
      if (width) {
        textToScale.style["transform"] = "scaleX(1)";
        if (width > this.initialWidth) {
          textToScale.style["transform"] = `scaleX(${
            this.initialWidth / width
          })`;
          textToScale.style["transform-origin"] = "left bottom";
        }
      }
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import "~@/assets/less/mixins";

@corner: 1.5mm;
@thick-border-width: 1mm;
@thin-border-width: 0.4mm;
@thinner-border-width: 0.2mm;

@media print {
  * {
    -webkit-transition: none !important;
    transition: none !important;
    zoom: 1 !important;
  }
}

.um-invisible {
  opacity: 0;
}

.editable {
  &:hover {
    .um-invisible {
      opacity: 1;
    }
  }

  .delete-button {
    color: #ccc;
    cursor: pointer;
    position: absolute;
    right: -3mm;
    top: -3mm;
    z-index: 100;
    &:hover {
      color: red;
    }
  }

  /deep/ .editor {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 3;
  }

  .attack .editor {
    cursor: pointer;
  }

  .attack-type:hover img {
    box-shadow: @editable-glow;
  }

  /deep/ .invalid {
    visibility: hidden;
  }

  /deep/ .value {
    + .editor {
      left: 68%;
      height: 50%;
      width: auto;
      visibility: hidden;
      &:hover {
        visibility: visible;
      }
    }

    &:hover + .editor {
      visibility: visible;
    }
  }

  .move-editor {
    &:hover {
      text-shadow: @editable-glow;
    }
  }

  /deep/ .hp {
    .editor {
      height: 18mm;
      width: 10mm;
      top: -6.5mm;
      left: 12.5mm;
      visibility: hidden;
    }

    &:hover {
      text-shadow: @editable-glow;
      .editor {
        visibility: visible;
      }
    }
  }

  /deep/ .quantity {
    .editor {
      height: 18mm;
      width: 10mm;
      top: -6mm;
      left: 6.5mm;
      visibility: hidden;
    }

    &:hover .editor {
      visibility: visible;
    }
  }

  .content {
    cursor: text;

    &:hover {
      box-shadow: @editable-glow;
    }
  }
}

.inner-border {
  background: var(--inner-border-colour);
  border-color: var(--inner-border-colour);
}

.main-wrapper {
  border-radius: @corner;
}

.printnplay {
  .panel,
  .panel.abilities {
    background: #fff;

    color: #000;
  }
}

.panel {
  position: relative;
  background: #000;
  color: #fff;

  .mixed-case {
    font-family: League Gothic, sans-serif;
  }

  &.hero {
    border-radius: @corner @corner 0 0;
  }

  &.abilities {
    height: 68.1mm + 2 * @thick-border-width;
    border-top: @thick-border-width solid var(--inner-border-colour);
    border-radius: 0 0 @corner @corner;

    background: var(--highlight-colour);
    background-image: var(--background-pattern);
    color: var(--contrast-colour);

    .section {
      height: 100%;
      padding-top: 4.7mm;
      padding-bottom: 4.7mm;
    }
  }

  .xl {
    font-size: 9mm;
    white-space: nowrap;
  }

  .content {
    height: 100%;
    margin-left: 3.9mm; // gutter width
    margin-right: 3.9mm;

    .scale-text {
      display: inline-block;
    }
  }
}

.section {
  &.name {
    height: 11.5mm; // Has this included the border?
    padding-top: 1.7mm;
  }
}

.gutter {
  float: left;
  height: 100%;
  width: 3.9mm; // includes border
  border-right: @thinner-border-width solid var(--inner-border-colour);
  position: relative;

  div {
    color: #fff;

    user-select: none;
    bottom: 1mm;
    left: 3.4mm;
    white-space: nowrap;
    padding: 0;
    margin: 0;
    transform: rotate(-90deg);
    position: absolute;
    transform-origin: left bottom;
    text-align: left;
    text-transform: uppercase;
    font-size: 2.5mm;
  }
}

.special {
  height: 100%;
  width: 100%;
  float: left;

  .content span {
    display: inline-block;
    width: 100%;
    height: 100%;
  }
}
</style>
